import React, { forwardRef, ReactNode, CSSProperties, useState, useEffect, useRef, useMemo, useContext } from 'react';
import { DOMProps } from '@react-types/shared';
import { mergeRefs } from '@direct-frontend/stdlib';

import { useHStack } from '../../../hooks/useStack';
import { TestProps } from '../../../internal/types/testTypes';
import { cn } from '../../../lib/css/className/className';
import './Cell.css';
import { InnerBordersContext } from '../context';

const cls = cn('TableCell');

export interface TableCellProps extends TestProps, DOMProps {
    /**
     * HTML-элемент ячейки
     * @default td
     */
    as?: 'td' | 'th';

    /**
     * Область заголовочной ячейки
     */
    scope?: 'col' | 'row' | 'colgroup' | 'rowgroup';

    /**
     * Дочерние элементы
     */
    children?: ReactNode;

    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Инлайновые стили ячейки
     */
    style?: CSSProperties;

    /**
     * Отключает правый разделитель
     */
    withoutRightBorder?: boolean;
    /**
     * охватывает заданное количество колонок по горизонтали
     */
    colspan?: number;
    /**
     * Добавляет прилипание колонок по левому краю
     */
    sticky?: boolean;
}

/**
 * Компонент для добавления ячейки в таблице
 */
export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(function TableCell(
    {
        as: Component = 'td',
        scope,
        colspan,
        sticky,
        style,
        withoutRightBorder,
        children,
        className,
        'data-testid': dataTestId = 'TableCell',
    },
    ref,
) {
    const stackClasses = useHStack({});
    const cellRef = useRef<HTMLTableCellElement | null>(null);
    const mergedRef = useMemo(() => mergeRefs(ref, cellRef), [ref]);
    const [leftPosition, setLeftPosition] = useState<number | null>(null);
    const innerBorders = useContext(InnerBordersContext);

    useEffect(() => {
        const cellElement = cellRef.current;

        if (sticky && cellElement && cellElement.parentElement && !cellElement.style.left) {
            setLeftPosition(
                cellElement.getBoundingClientRect().left - cellElement.parentElement.getBoundingClientRect().left,
            );
        }
    }, [mergedRef, sticky]);

    return (
        <Component
            colSpan={colspan}
            scope={scope}
            ref={mergedRef}
            style={{
                ...style,
                ...(sticky &&
                    leftPosition !== null && {
                        left: leftPosition,
                    }),
            }}
            className={cls(
                '',
                {
                    withoutRightBorder: withoutRightBorder || innerBorders === 'none' || innerBorders === 'horizontal',
                    sticky,
                },
                [className],
            )}
            data-testid={dataTestId}
        >
            <div className={cls('wrapper', [stackClasses])}>{children}</div>
        </Component>
    );
});
